<template>
  <div class="relative">
    <!-- Profile Button when authenticated -->
    <button 
      v-if="isAuthenticated"
      @click.stop="toggleDropdown"
      class="flex items-center space-x-2 text-white hover:bg-primary-600 px-3 py-2 rounded-lg"
    >
      <div class="w-8 h-8 rounded-full bg-primary-400 flex items-center justify-center text-white font-semibold">
        {{ userInitials }}
      </div>
      <span class="inline">{{ user?.name || user?.email || 'User' }}</span>
    </button>

    <!-- Login Button when not authenticated -->
    <router-link
      v-else
      to="/login"
      class="flex items-center space-x-2 text-white hover:bg-primary-600 px-3 py-2 rounded-lg"
    >
      <span>Login</span>
    </router-link>

    <!-- Dropdown Menu -->
    <div 
      v-if="isOpen && isAuthenticated"
      class="absolute left-0 md:left-auto md:right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50"
    >
      <div class="px-4 py-2 border-b border-gray-200">
        <p class="text-sm font-semibold text-gray-800">{{ user?.name || user?.email }}</p>
        <p v-if="user?.name && user?.email" class="text-xs text-gray-500">{{ user?.email }}</p>
        <div class="mt-2 flex items-center justify-between">
           <span v-if="subscription" :class="badgeClass" class="text-xs px-2 py-0.5 rounded-full font-medium border">
             {{ subscription.tier }}
           </span>
           <span v-else class="text-xs text-red-400">
             Plan info unavailable
           </span>
        </div>
      </div>
      
      <router-link 
        to="/subscription"
        @click="isOpen = false"
        class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 !text-gray-700"
      >
        Subscription & Billing
      </router-link>

      <button 
        @click="handleLogout"
        class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
      >
        Logout
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

export default {
  name: 'UserProfile',
  setup() {
    const store = useStore();
    const router = useRouter();
    const isOpen = ref(false);

    const user = computed(() => store.state.auth.user);
    const isAuthenticated = computed(() => store.state.auth.isAuthenticated);
    const subscription = computed(() => store.getters['auth/subscription']);

    const badgeClass = computed(() => {
      const tier = subscription.value?.tier;
      switch (tier) {
        case 'TRADER': return 'bg-primary-50 text-primary-700 border-primary-200';
        case 'PRO': return 'bg-green-50 text-green-700 border-green-200';
        default: return 'bg-gray-50 text-gray-600 border-gray-200';
      }
    });
    
    const userInitials = computed(() => {
      if (user.value?.name) {
        return user.value.name
          .split(' ')
          .map(n => n[0])
          .join('')
          .toUpperCase()
          .slice(0, 2);
      }
      if (user.value?.email) {
        return user.value.email[0].toUpperCase();
      }
      return 'U';
    });

    const toggleDropdown = () => {
      isOpen.value = !isOpen.value;
    };

    const handleLogout = () => {
      // Clear auth state
      store.dispatch('auth/logout');
      
      // Redirect to login
      router.push('/login');
    };

    // Close dropdown when clicking outside
    const closeDropdown = (event) => {
      // Only close if click is outside the dropdown
      const isClickInside = event.target.closest('.relative');
      if (!isClickInside && isOpen.value) {
        isOpen.value = false;
      }
    };

    // Add and remove global click listener
    onMounted(() => {
      document.addEventListener('click', closeDropdown);
    });

    onUnmounted(() => {
      document.removeEventListener('click', closeDropdown);
    });

    return {
      isOpen,
      user,
      userInitials,
      subscription,
      badgeClass,
      toggleDropdown,
      handleLogout,
      isAuthenticated
    };
  }
};
</script> 